import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import { useDialog } from "../../DialogContext";
import { updateStatus } from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import { backend_api } from "../../../static/server";
import axios from "axios";

const ActionMenuForm = ({
  form_type,
  body,
  BillId,
  fetchBills,
  setActiveFilter,
}) => {
  const { closeDialog } = useDialog();
  const dispatch = useDispatch();
  const { token } = useSelector((data) => data.user);
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [users, setUsers] = useState([]);
  const [nextUrl, setNextUrl] = useState(
    `${backend_api}team/create-employee/?is_active=True`
  );
  const [usersLoading, setUsersLoading] = useState(false);

  const fetchUsers = useCallback(
    async (url = nextUrl) => {
      if (!url || usersLoading) return;
      setUsersLoading(true);
      try {
        const res = await axios.get(url, {
          headers: { Authorization: `Token ${token}` },
        });
        setUsers((prev) => [...prev, ...(res.data.results || [])]);
        setNextUrl(res.data.next);
      } catch (err) {
        console.error("Failed to load users", err);
      } finally {
        setUsersLoading(false);
      }
    },
    [nextUrl, token, usersLoading]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (
        scrollHeight - scrollTop - clientHeight < 20 &&
        nextUrl &&
        !usersLoading
      ) {
        fetchUsers();
      }
    },
    [fetchUsers, nextUrl, usersLoading]
  );

  const handleSubmitApproval = async () => {
    try {
      setApprovedLoading(true);
      await dispatch(
        updateStatus({
          id: BillId,
          data: body,
          token: token,
        })
      );

      if (typeof fetchBills === "function") {
        fetchBills();
      }

      if (typeof setActiveFilter === "function") {
        setActiveFilter("pending");
      }
      closeDialog();
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setApprovedLoading(false);
    }
  };

  const onSubmitAskApproval = async (data) => {
    try {
      setSubmitLoading(true);

      const approvalData = {
        ...body,
        ask_user_approval: data.ask_user_approval || [],
      };

      await dispatch(
        updateStatus({
          id: BillId,
          data: approvalData,
          token: token,
        })
      );

      if (typeof fetchBills === "function") {
        fetchBills();
      }

      if (typeof setActiveFilter === "function") {
        setActiveFilter("pending");
      }

      closeDialog();
    } catch (error) {
      console.error("Error asking for approval:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box>
      {form_type === "submitAproval" && (
        <form onSubmit={handleSubmit(onSubmitAskApproval)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "2rem",
            }}
          >
            <Controller
              name="ask_user_approval"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <FormControl fullWidth>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={users || []}
                    getOptionLabel={(option) =>
                      `${option.first_name || ""} ${
                        option.last_name || ""
                      }`.trim() || "Unknown User"
                    }
                    value={
                      users?.filter((user) =>
                        Array.isArray(field.value)
                          ? field.value.includes(user.id)
                          : false
                      ) || []
                    }
                    onChange={(_, selectedUsers) => {
                      const selectedUserIds = selectedUsers.map(
                        (user) => user.id
                      );
                      field.onChange(selectedUserIds);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    ListboxProps={{
                      onScroll: handleScroll,
                    }}
                    loading={usersLoading}
                    renderTags={(value, getTagProps) => (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          maxWidth: "16rem",
                          overflowX: "auto",
                          borderRadius: "4px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {value.map((option, index) => (
                          <Chip
                            key={option.id || index}
                            label={
                              `${option.first_name || ""} ${
                                option.last_name || ""
                              }`.trim() || "Unknown User"
                            }
                            size="small"
                            {...getTagProps({ index })}
                            sx={{
                              fontSize: "0.8rem",
                              "& .MuiChip-label": {
                                padding: "0 6px",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Users for Approval"
                        size="small"
                        error={!!errors.ask_user_approval}
                        helperText={errors.ask_user_approval?.message}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </Box>
          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "0.5rem",
            }}
          >
            <Button variant="outlined" onClick={closeDialog}>
              Cancel
            </Button>
            <LoadingButton
              size="small"
              loading={submitLoading}
              variant="contained"
              color="primary"
              type="submit"
            >
              Ask for Approval
            </LoadingButton>
            <LoadingButton
              size="small"
              variant="contained"
              color="primary"
              onClick={handleSubmitApproval}
              loading={approvedLoading}
            >
              Approve Bill
            </LoadingButton>
          </Box>
        </form>
      )}
    </Box>
  );
};

export default ActionMenuForm;
